/**
 * Who receives a notification, and on which channel.
 * CommonJS only. No object spread: this file is required from the app and functions.
 *
 * Billing notices are always on. The homeowner master switch (email_enabled)
 * suppresses every other email and leaves push alone.
 * Read-only (unpaid) homeowners receive billing notices only.
 * Sponsorless homeowners do not receive project updates or Home History Report requests.
 * Personal reminders go to paid and free-window sponsorless homeowners,
 * and to sponsored homeowners through grace. They do not follow the agent's subscription.
 */

const { homeownerEntitlement } = require('./homeownerEntitlement');
const { subscriptionAllowsAccess } = require('../utils/subscriptionAccess');

const HOMEOWNER_PREF_FIELDS = [
  'email_enabled',
  'task_reminders',
  'task_reminders_email',
  'task_reminders_push',
  'project_updates_email',
  'project_updates_push',
  'weekly_summary',
  'monthly_digest_email',
  'monthly_digest_push',
  'report_requests_email',
  'report_requests_push',
  'expense_alerts',
  'push_enabled',
  'reminder_days_before',
];

const HOMEOWNER_BOOL_FIELDS = [
  'email_enabled',
  'task_reminders',
  'task_reminders_email',
  'task_reminders_push',
  'project_updates_email',
  'project_updates_push',
  'weekly_summary',
  'monthly_digest_email',
  'monthly_digest_push',
  'report_requests_email',
  'report_requests_push',
  'expense_alerts',
  'push_enabled',
];

const AGENT_EMAIL_FIELDS = {
  task_reminders: 'task_reminders_enabled',
  project_reminders: 'project_reminders_enabled',
  project_completed: 'project_completion_enabled',
  task_completed: 'task_completed_email',
  help_requests: 'help_requests_email',
  homeowner_moved: 'homeowner_moved_email',
  weekly_client_activity: 'weekly_agent_summary_enabled',
};

const AGENT_PUSH_FIELDS = {
  task_reminders: 'task_reminders_push',
  project_reminders: 'project_reminders_push',
  project_completed: 'project_completion_push',
  task_completed: 'task_completed_push',
  help_requests: 'help_requests_push',
  homeowner_moved: 'homeowner_moved_push',
  weekly_client_activity: 'weekly_agent_summary_push',
};

const AGENT_PUSH_DEFAULT_OFF = {
  weekly_client_activity: true,
};

function explicit(value) {
  return value === true || value === false;
}

function preferenceRow(value) {
  if (Array.isArray(value)) return value[0] || null;
  if (value && typeof value === 'object') return value;
  return null;
}

function sponsorId(client) {
  if (!client || typeof client !== 'object') return '';
  const assigned = client.assigned_agent_id;
  const owner = client.agent_id;
  return String(assigned || owner || '').trim();
}

function sponsorProfile(client) {
  if (!client || typeof client !== 'object') return null;
  if (client.agent && typeof client.agent === 'object') return client.agent;
  return null;
}

function sponsorIsLive(client, now) {
  const id = sponsorId(client);
  const profile = sponsorProfile(client);
  if (!id || !profile) return false;
  return subscriptionAllowsAccess(profile, now || new Date());
}

/**
 * @returns {{
 *   reason: string,
 *   sponsored: boolean,
 *   readOnly: boolean,
 *   personalReminders: boolean,
 *   billingWarning: boolean,
 *   settingsAudience: 'sponsored'|'sponsorless'|'readonly'
 * }}
 */
function notificationContext(client, now) {
  const decision = homeownerEntitlement(client, now);
  const live = sponsorIsLive(client, now);
  const sponsored = decision.reason === 'sponsored' || (decision.reason === 'grace' && live);
  const readOnly = decision.reason === 'unpaid';
  const paidOrFree = decision.reason === 'sponsorless_paid' || decision.reason === 'sponsorless_free_period';
  const ownGrace = decision.reason === 'grace' && !live;
  let settingsAudience = 'sponsorless';
  if (readOnly) settingsAudience = 'readonly';
  else if (sponsored) settingsAudience = 'sponsored';
  return {
    reason: decision.reason,
    sponsored: sponsored,
    readOnly: readOnly,
    personalReminders: sponsored || paidOrFree,
    billingWarning: !sponsored && (ownGrace || readOnly),
    settingsAudience: settingsAudience,
  };
}

function categoryOpen(context, category) {
  if (category === 'billing') return true;
  if (!context || context.readOnly) return false;
  if (!context.sponsored && (category === 'project_updates' || category === 'report_requests')) return false;
  return true;
}

function homeownerChannel(prefs, category, channel) {
  const row = prefs || {};
  if (channel === 'push' && row.push_enabled === false) return false;
  if (category === 'billing') return true;
  if (channel === 'email' && row.email_enabled === false) return false;
  if (category === 'task_reminders') {
    const key = channel === 'email' ? 'task_reminders_email' : 'task_reminders_push';
    if (explicit(row[key])) return row[key];
    if (row.task_reminders === false) return false;
    return true;
  }
  if (category === 'monthly_digest') {
    if (channel === 'push') {
      if (explicit(row.monthly_digest_push)) return row.monthly_digest_push;
      return false;
    }
    if (explicit(row.monthly_digest_email)) return row.monthly_digest_email;
    if (row.weekly_summary === false) return false;
    return true;
  }
  if (category === 'project_updates' || category === 'report_requests') {
    const key = category === 'project_updates'
      ? (channel === 'email' ? 'project_updates_email' : 'project_updates_push')
      : (channel === 'email' ? 'report_requests_email' : 'report_requests_push');
    if (explicit(row[key])) return row[key];
    return true;
  }
  return false;
}

function applyLegacyOptOut(notificationRow, legacyRow) {
  const row = {};
  const source = notificationRow || {};
  Object.keys(source).forEach((key) => {
    row[key] = source[key];
  });
  const legacy = legacyRow || {};
  const taskColumnsUnset = !explicit(row.task_reminders_email) && !explicit(row.task_reminders_push);
  if (taskColumnsUnset && legacy.task_reminders_enabled === false) row.task_reminders = false;
  if (!explicit(row.project_updates_email) && (legacy.project_reminders_enabled === false || legacy.project_completion_enabled === false)) {
    row.project_updates_email = false;
  }
  if (!explicit(row.monthly_digest_email) && legacy.weekly_summary_enabled === false) {
    row.monthly_digest_email = false;
  }
  return row;
}

function homeownerMaySend(context, prefs, category, channel) {
  if (!categoryOpen(context, category)) return false;
  return homeownerChannel(prefs, category, channel);
}

function agentChannel(prefs, kind, channel) {
  const fields = channel === 'push' ? AGENT_PUSH_FIELDS : AGENT_EMAIL_FIELDS;
  const key = fields[kind];
  if (!key) return false;
  const row = prefs || {};
  if (channel === 'push' && row.push_enabled === false) return false;
  if (explicit(row[key])) return row[key];
  if (channel === 'push' && AGENT_PUSH_DEFAULT_OFF[kind]) return false;
  return true;
}

/**
 * Both the agent's switch and the homeowner's category must allow a message
 * the agent sends to a homeowner.
 */
function sharedMaySend(context, homeownerPrefs, agentPrefs, homeownerCategory, agentKind, channel) {
  if (!homeownerMaySend(context, homeownerPrefs, homeownerCategory, channel)) return false;
  if (agentKind && !agentChannel(agentPrefs, agentKind, channel)) return false;
  return true;
}

function billingWarningKind(context) {
  if (!context || !context.billingWarning) return '';
  if (context.readOnly) return 'readonly';
  return 'grace';
}

module.exports = {
  HOMEOWNER_PREF_FIELDS,
  HOMEOWNER_BOOL_FIELDS,
  AGENT_EMAIL_FIELDS,
  AGENT_PUSH_FIELDS,
  preferenceRow,
  notificationContext,
  categoryOpen,
  homeownerChannel,
  applyLegacyOptOut,
  homeownerMaySend,
  agentChannel,
  sharedMaySend,
  billingWarningKind,
  sponsorIsLive,
};
